import { useState } from "react";
import {
  Button,
  Card,
  CardContent,
  Stack,
  Box,
  Typography,
  Skeleton,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { MY_PERF_SCROLL_LIST_PB } from "../../Dashboard/layouts";

const columnsPR = [
  { id: "daysOpen", label: "Days Open" },
  { id: "noOfReferrals", label: "Number of Referrals" },
];

const columnsHCP = [
  { id: "hcpName", label: "HCP Name" },
  { id: "pendingReferrals", label: "Pending Referrals" },
];

function PendingReferralsTable({ tabValue, rowsPR, rowsHCP, isLoading }) {
  if (isLoading) {
    return (
      <TableContainer
        sx={{
          maxHeight: "210px",
          overflowY: "auto",
          pr: 0.5,
        }}
      >
        <Stack spacing={1.2} sx={{ py: 1, pb: MY_PERF_SCROLL_LIST_PB }}>
          {[...Array(4)].map((_, idx) => (
            <Skeleton key={idx} variant="rounded" height={24} />
          ))}
        </Stack>
      </TableContainer>
    );
  }

  const columns = tabValue === 0 ? columnsPR : columnsHCP;
  const rows = tabValue === 0 ? rowsPR : rowsHCP;

  const hasRows = Array.isArray(rows) && rows.length > 0;

  return (
    <TableContainer
      sx={{
        maxHeight: "210px",
        overflowY: "auto",
        pr: 0.5,
      }}
    >
      {!hasRows ? (
        <Box
          sx={{
            height: "100%",
            minHeight: 80,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#FFFFFF",
          }}
        >
          <Typography fontSize={10} sx={{ color: "#000", textAlign: "center" }}>
            No data found
          </Typography>
        </Box>
      ) : (
        <Box sx={{ pb: MY_PERF_SCROLL_LIST_PB }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell
                    key={col.id}
                    sx={{
                      border: "none",
                      textAlign: "center",
                      fontFamily: '"Helvetica Neue", Arial, sans-serif',
                      fontSize: "12px",
                      fontWeight: 700,
                      color: "#454545",
                      backgroundColor: "#EEF0F1",
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, idx) => (
                <TableRow key={idx}>
                  {Object.values(row).map((val, i) => (
                    <TableCell
                      key={i}
                      sx={{
                        border: "none",
                        borderBottom: "1px solid #E0E0E0",
                        textAlign: "center",
                        fontFamily: '"Helvetica Neue", Arial, sans-serif',
                        fontSize: "14px",
                        fontWeight: 400,
                        color: "#454545",
                        backgroundColor: "#FFFFFF",
                      }}
                    >
                      {val}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

      )}
    </TableContainer>
  );
}

export default function PendingReferralsCard({
  title = "Summary",
  pendingReferrals = 0,
  rowsPR = [],
  rowsHCP = [],
  isLoading = false,
  onExplore,
}) {
  const [tabValue, setTabValue] = useState(0);

  const handleChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        borderRadius: "8px",
        border: "0.5px solid #F0F0F0",
        boxShadow: "1px 4px 4px 0px #00000008",
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mb: "20px",
            width: "100%",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              fontSize: "14px",
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>
          {onExplore && (
            <Button
              variant="text"
              size="small"
              onClick={() => onExplore()}
              sx={{
                textTransform: "none",
                fontSize: 14,
                fontWeight: 600,
                color: "#D96B04",
                minWidth: "auto",
                p: 0,
                flexShrink: 0,
                marginRight: "30px",
              }}
            >
              Explore
            </Button>
          )}
        </Box>
        <Stack spacing={2}>
          <Box sx={{ backgroundColor: "#F7F9FB", py: 1, px: 4 }}>
            {isLoading ? (
              <Skeleton variant="text" width={60} height={36} />
            ) : (
              <Typography
                sx={{
                  fontFamily: '"Helvetica Neue", Arial, sans-serif',
                  fontWeight: 600,
                  fontSize: "22px",
                }}
              >
                {pendingReferrals}
              </Typography>
            )}
            <Stack direction="row" mx="auto" spacing={0.5}>
              <Typography
                sx={{
                  fontFamily: '"Helvetica Neue", Arial, sans-serif',
                  fontWeight: 400,
                  fontSize: "12px",
                }}
              >
                Total no. of Pending Referrals
              </Typography>
              <Tooltip
                title="Total count of open referrals that are yet to be converted into starts for your region."
                placement="right"
                slotProps={{
                  popper: {
                    modifiers: [
                      { name: "offset", options: { offset: [0, -10] } },
                    ],
                  },
                  tooltip: {
                    sx: {
                      bgcolor: "#FFFFFF",
                      color: "#2D2D2D",
                      border: "1px solid #E6E6E6",
                      boxShadow: "0px 1px 2px 0px #0000000D",
                    },
                  },
                }}
              >
                <Box
                  aria-label="Info about region rank"
                  sx={{ lineHeight: 0, display: "inline-flex" }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 15, color: "#A0A0A0" }} />
                </Box>
              </Tooltip>
            </Stack>
          </Box>
          <Box>
            <Tabs
              value={tabValue}
              onChange={handleChange}
              styles={{}}
              sx={{
                minHeight: 32,
                borderBottom: "1px solid #F7F9FB",
                fontSize: "12px",
                fontFamily: '"Helvetica Neue", Arial, sans-serif',
                fontWeight: 500,
                "& .MuiTab-root": {
                  minHeight: 32,
                  px: 1.5,
                  // fontSize: "10px",
                  textTransform: "none",
                  color: "#000",
                  borderBottom: "2px solid transparent",
                  transition: "border-color 150ms ease",
                  mr: 2,
                },
                "& .MuiTab-root:hover": {
                  borderBottomColor: "#000000",
                },
                "& .MuiTab-root.Mui-selected": {
                  color: "#D96B04",
                  fontWeight: 600,
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#D96B04",
                  height: 2,
                },
              }}
            >
              <Tab label="Pending Referrals" value={0} />
              <Tab label="HCPs with Pending Referrals" value={1} />
            </Tabs>
            <PendingReferralsTable
              tabValue={tabValue}
              rowsPR={rowsPR}
              rowsHCP={rowsHCP}
              isLoading={isLoading}
            />
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
